import { Capacitor } from '@capacitor/core';

/**
 * Audio helper utilities for cross-platform audio handling
 */

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function getPlatform(): string {
  return Capacitor.getPlatform();
}

export function shouldUseNativeAudio(): boolean {
  const platform = getPlatform();
  // Temporarily disable native audio for iOS due to Firebase Storage URL issues
  // TODO: Re-enable when iOS audio loading is fixed
  if (platform === 'ios') {
    return false;
  }
  return isNativePlatform() && platform === 'android';
}

export function getAudioConfig() {
  const platform = getPlatform();
  
  // Platform-specific configurations
  const configs = {
    ios: {
      focus: false, // Don't interrupt other apps
      preloadSimple: false, // Use full audio player
      audioChannelNum: 1,
    },
    android: {
      focus: false, // Don't interrupt other apps
      audioChannelNum: 1,
    },
    web: {
      preload: 'auto',
      crossOrigin: 'anonymous',
    }
  };

  return configs[platform as keyof typeof configs] || configs.web;
}

export function handleAudioError(error: any, platform: string): string {
  // Platform-specific error messages
  if (platform === 'ios') {
    if (error.message?.includes('decode')) {
      return 'iOS ses formatı hatası. Ses dosyası yeniden yükleniyor...';
    }
    if (error.message?.includes('NotAllowedError')) {
      return 'iOS ses izni gerekiyor. Lütfen ekrana dokunun.';
    }
  }
  
  if (platform === 'android') {
    if (error.message?.includes('MEDIA_ERROR_IO')) {
      return 'Android ses dosyası yükleme hatası.';
    }
  }

  // Generic error messages
  if (error.message?.includes('network')) {
    return 'Ağ bağlantısı hatası. Lütfen internet bağlantınızı kontrol edin.';
  }

  return 'Ses yükleme hatası oluştu.';
}

export async function preloadAudioWithRetry(
  preloadFn: () => Promise<void>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<void> {
  let lastError: Error | null = null;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      await preloadFn();
      return; // Success
    } catch (error) {
      lastError = error as Error;
      
      if (i < maxRetries - 1) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }
  
  throw lastError || new Error('Failed to preload audio after retries');
}